import React, { useEffect, useRef, useState } from "react";
import Grain from "../Grain/Grain";
import { useAppLinks } from "../../hooks/useAppLinks";
import { prefersReducedMotion } from "../../utils/motion";
import "./styles.css";

const FLIP_WORDS = ["opera.", "indica.", "decide."];
const DEMO_HISTORY = "Paciente João, 42 anos, obstrução nasal crônica há 2 anos.";
const DEMO_REPORT = "Desvio de septo com hipertrofia de conchas inferiores.";

// Fases da demo, em ordem.
const DEMO = { TYPING_HISTORY: 0, TYPING_REPORT: 1, TYPED: 2, ORDER: 3, STAMP: 4 };

const useFlipIndex = (length) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % length), 2600);
    return () => clearInterval(id);
  }, [length]);

  return index;
};

// Digita o histórico, depois o laudo, mostra o pedido e o carimbo, e recomeça.
const useTypingDemo = () => {
  const reduced = prefersReducedMotion();
  const [history, setHistory] = useState(reduced ? DEMO_HISTORY : "");
  const [report, setReport] = useState(reduced ? DEMO_REPORT : "");
  const [phase, setPhase] = useState(reduced ? DEMO.STAMP : DEMO.TYPING_HISTORY);

  useEffect(() => {
    if (reduced) return;

    const timers = new Set();
    const later = (fn, ms) => {
      const id = setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };

    const type = (setText, text, done) => {
      let typed = 0;
      const tick = () => {
        typed += 1;
        setText(text.slice(0, typed));
        if (typed < text.length) later(tick, 36);
        else done();
      };
      later(tick, 36);
    };

    const run = () => {
      setHistory("");
      setReport("");
      setPhase(DEMO.TYPING_HISTORY);
      type(setHistory, DEMO_HISTORY, () => {
        setPhase(DEMO.TYPING_REPORT);
        type(setReport, DEMO_REPORT, () => {
          setPhase(DEMO.TYPED);
          later(() => setPhase(DEMO.ORDER), 420);
          later(() => setPhase(DEMO.STAMP), 900);
          later(run, 7200);
        });
      });
    };

    later(run, 1200);
    return () => timers.forEach(clearTimeout);
  }, [reduced]);

  return { history, report, phase };
};

const Hero = () => {
  const links = useAppLinks();
  const flipIndex = useFlipIndex(FLIP_WORDS.length);
  const { history, report, phase } = useTypingDemo();
  const [loaded, setLoaded] = useState(false);
  const [hovering, setHovering] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setLoaded(true), 50);
    return () => clearTimeout(id);
  }, []);

  // Spotlight do grid segue o mouse.
  const handleMouseMove = (event) => {
    const section = sectionRef.current;
    const rect = section.getBoundingClientRect();
    section.style.setProperty("--mx", `${event.clientX - rect.left}px`);
    section.style.setProperty("--my", `${event.clientY - rect.top}px`);
    setHovering(true);
  };

  const classes = ["hero", "dark", loaded && "loaded", hovering && "hovering"].filter(Boolean).join(" ");

  return (
    <section
      ref={sectionRef}
      className={classes}
      id="hero"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHovering(false)}
    >
      <div className="glow glow-a" />
      <div className="glow glow-b" />
      <div className="gridlines" />
      <div className="spot" />
      <div className="sq" style={{ width: 90, height: 90, top: "18%", right: "8%" }} />
      <div className="sq" style={{ width: 54, height: 54, bottom: "16%", left: "6%", animationDelay: "2s" }} />
      <div
        className="sq"
        style={{ width: 120, height: 120, top: "62%", right: "32%", animationDelay: "4s", opacity: 0.5 }}
      />
      <Grain variant="hard" />
      <Grain variant="soft" />

      <div className="container">
        <div className="hero-inner">
          <div className="hero-copy">
            <div className="hero-rail load l1">
              <span className="bar" />
              <span>Feito para o centro cirúrgico</span>
            </div>
            <div className="pill load l1">
              <span className="dot" />A IA que está do lado do cirurgião
            </div>
            <h1 className="display load l2">
              Cirurgião{" "}
              <span className="flip">
                {FLIP_WORDS.map((word, i) => (
                  <span key={word} className={i === flipIndex ? "active" : ""} aria-hidden={i !== flipIndex}>
                    {word}
                  </span>
                ))}
              </span>
              <br />O LetsDoc cuida do pedido.
            </h1>
            <p className="hero-sub load l3">
              Não é IA genérica de saúde. É a parceira da sua rotina cirúrgica: pedido, OPME e recurso
              de glosa prontos em segundos. <b>Você no bisturi. A burocracia com a gente.</b>
            </p>
            <div className="hero-cta load l4">
              <a className="btn btn-primary" href={links.signup}>
                Criar conta grátis
              </a>
              <a className="btn btn-outline" href={links.login}>
                Já sou cirurgião cadastrado
              </a>
            </div>
            <p className="hero-note load l5">Exclusivo para cirurgiões. Sem cartão. Plano básico grátis.</p>
          </div>

          <div className="demo load l6" aria-hidden="true">
            <div className="bar">
              <i />
              <i />
              <i />
              <span>app.letsdoc.ai</span>
            </div>
            <div className="body">
              <div className="field">
                <span className="lb">Histórico</span>
                <span>{history}</span>
                {phase === DEMO.TYPING_HISTORY && <span className="cursor" />}
              </div>
              <div className="field">
                <span className="lb">Laudo</span>
                <span>{report}</span>
                {phase === DEMO.TYPING_REPORT && <span className="cursor" />}
              </div>
              <div className={`out ${phase >= DEMO.ORDER ? "show" : ""}`}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                <span>
                  <b>Pedido gerado</b> com pacote de OPME anexado.
                </span>
              </div>
            </div>
            <span className={`stamp stamp-blue demo-stamp ${phase >= DEMO.STAMP ? "show" : ""}`}>Aprovado</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
